/**
 * Blockbench Parser - Convierte archivos .bbmodel a formato interno de Cyberon
 * 
 * Formato .bbmodel:
 * - meta: información del modelo (format_version, model_format, box_uv)
 * - resolution: {width, height} - tamaño de la textura
 * - elements: array de cubos con from, to, origin, uv, faces, rotation, etc.
 * - outliner: jerarquía de bones (grupos)
 * - textures: array de texturas con source (base64)
 */

/**
 * Parsea un archivo .bbmodel y extrae toda la información
 */
export const parseBlockbench = (bbmodelData) => {
  const {
    meta = {},
    name = 'model',
    geometry_name,
    resolution = { width: 64, height: 64 },
    elements = [],
    outliner = [],
    textures = [],
  } = bbmodelData;

  // Determinar si usa box_uv o per-face UV
  const useBoxUV = meta.box_uv !== false;

  // Parsear elementos (cubos)
  const parsedCubes = elements.map((element) => parseCube(element, resolution, useBoxUV));

  // Parsear jerarquía de bones desde outliner
  const bones = parseOutliner(outliner, parsedCubes);

  // Parsear texturas
  const parsedTextures = textures.map((tex) => ({
    id: tex.uuid || tex.id,
    name: tex.name,
    source: tex.source, // data:image/png;base64,...
    width: tex.width || resolution.width,
    height: tex.height || resolution.height,
  }));

  return {
    name,
    identifier: geometry_name || `geometry.${name}`,
    resolution,
    useBoxUV,
    cubes: parsedCubes,
    bones,
    textures: parsedTextures,
    primaryTexture: parsedTextures[0]?.source || null,
  };
};

/**
 * Parsea un cubo individual
 */
const parseCube = (element, resolution, useBoxUV) => {
  const {
    uuid,
    name = 'cube',
    from = [0, 0, 0],
    to = [1, 1, 1],
    origin = [0, 0, 0],
    rotation = [0, 0, 0],
    uv_offset = [0, 0],
    faces = {},
    inflate = 0,
    visibility = true,
    locked = false,
  } = element;

  // Calcular size
  const size = [
    to[0] - from[0],
    to[1] - from[1],
    to[2] - from[2],
  ];

  // Posición (from es el origen del cubo)
  const position = from;

  // Convertir rotación a Three.js (Blockbench usa grados)
  const rotationRad = rotation.map((r) => (r * Math.PI) / 180);

  return {
    id: uuid || `cube_${Math.random().toString(36).substr(2, 9)}`,
    name,
    from,
    to,
    size,
    position,
    origin,
    rotation: rotationRad,
    rotationDegrees: rotation,
    uv_offset,
    faces: parseFaces(faces, useBoxUV, uv_offset, size, resolution),
    inflate,
    visible: visibility,
    locked,
    useBoxUV,
  };
};

/**
 * Parsea las caras UV del cubo
 * En Blockbench box_uv: uv_offset es [x, y] y las caras se calculan automáticamente
 * En per-face UV: cada cara tiene su propio uv y uv_size
 */
const parseFaces = (faces, useBoxUV, uvOffset, size, resolution) => {
  if (useBoxUV) {
    // Box UV: calcular caras automáticamente basado en offset y size
    const [ox, oy] = uvOffset;
    const [w, h, d] = size;
    
    return {
      north: {  // Front (Z-)
        uv: [ox + d, oy + d, ox + d + w, oy + d + h],
        texture: 0,
      },
      south: {  // Back (Z+)
        uv: [ox + 2 * d + w, oy + d, ox + 2 * d + 2 * w, oy + d + h],
        texture: 0,
      },
      east: {   // Right (X+)
        uv: [ox, oy + d, ox + d, oy + d + h],
        texture: 0,
      },
      west: {   // Left (X-)
        uv: [ox + d + w, oy + d, ox + 2 * d + w, oy + d + h],
        texture: 0,
      },
      up: {     // Top (Y+)
        uv: [ox + d, oy, ox + d + w, oy + d],
        texture: 0,
      },
      down: {   // Bottom (Y-)
        uv: [ox + d + w, oy, ox + d + 2 * w, oy + d],
        texture: 0,
      },
    };
  } else {
    // Per-face UV
    const result = {};
    ['north', 'south', 'east', 'west', 'up', 'down'].forEach((face) => {
      if (faces[face]) {
        result[face] = {
          uv: faces[face].uv || [0, 0, 1, 1],
          rotation: faces[face].rotation || 0,
          texture: faces[face].texture !== undefined ? faces[face].texture : 0,
        };
      }
    });
    return result;
  }
};

/**
 * Parsea el outliner (jerarquía de bones/grupos)
 */
const parseOutliner = (outliner, cubes) => {
  const cubesMap = new Map(cubes.map((c) => [c.id, c]));
  const bones = [];

  const processItem = (item, parentName = null) => {
    if (typeof item === 'string') {
      // Es un cubo (UUID), no un grupo
      const cube = cubesMap.get(item);
      if (cube) {
        cube.parent = parentName;
      }
      return;
    }

    // Es un grupo/bone
    const bone = {
      name: item.name || 'bone',
      id: item.uuid || item.name,
      origin: item.origin || [0, 0, 0],
      rotation: (item.rotation || [0, 0, 0]).map((r) => (r * Math.PI) / 180),
      pivot: item.origin || [0, 0, 0],
      parent: parentName,
      visible: item.visibility !== false,
      cubes: [],
      children: [],
    };

    // Procesar hijos
    if (item.children && Array.isArray(item.children)) {
      item.children.forEach((child) => {
        if (typeof child === 'string') {
          // Es un cubo
          const cube = cubesMap.get(child);
          if (cube) {
            cube.parent = bone.name;
            bone.cubes.push(cube.id);
          }
        } else {
          // Es un sub-grupo
          bone.children.push(child.name);
          processItem(child, bone.name);
        }
      });
    }

    bones.push(bone);
  };

  outliner.forEach((item) => processItem(item));

  // Si no hay bones, crear uno por defecto que contenga todos los cubos
  if (bones.length === 0 && cubes.length > 0) {
    bones.push({
      name: 'root',
      id: 'root',
      origin: [0, 0, 0],
      rotation: [0, 0, 0],
      pivot: [0, 0, 0],
      parent: null,
      visible: true,
      cubes: cubes.map((c) => c.id),
      children: [],
    });
    cubes.forEach((c) => (c.parent = 'root'));
  }

  return bones;
};

/**
 * Convierte el modelo parseado al formato interno de Cyberon
 */
export const toCyberonFormat = (parsedModel) => {
  return {
    name: parsedModel.name,
    identifier: parsedModel.identifier,
    resolution: parsedModel.resolution,
    useBoxUV: parsedModel.useBoxUV,
    cubes: parsedModel.cubes,
    bones: parsedModel.bones,
    texture: parsedModel.primaryTexture,
    textures: parsedModel.textures,
  };
};

/**
 * Convierte el modelo interno a formato Minecraft geometry.json
 */
export const toMinecraftGeometry = (cyberonModel) => {
  const { name, identifier, resolution, bones, cubes, useBoxUV } = cyberonModel;

  const cubesMap = new Map(cubes.map((c) => [c.id, c]));

  const mcBones = bones.map((bone) => {
    const boneCubes = bone.cubes
      .map((cubeId) => cubesMap.get(cubeId))
      .filter(Boolean)
      .map((cube) => {
        const cubeData = {
          origin: cube.from,
          size: cube.size,
        };

        if (useBoxUV) {
          cubeData.uv = cube.uv_offset;
        } else {
          cubeData.uv = {};
          Object.entries(cube.faces).forEach(([face, faceData]) => {
            cubeData.uv[face] = {
              uv: [faceData.uv[0], faceData.uv[1]],
              uv_size: [faceData.uv[2] - faceData.uv[0], faceData.uv[3] - faceData.uv[1]],
            };
          });
        }

        if (cube.rotationDegrees.some((r) => r !== 0)) {
          cubeData.rotation = cube.rotationDegrees;
        }

        if (cube.origin && cube.origin.some((v) => v !== 0)) {
          cubeData.pivot = cube.origin;
        }

        if (cube.inflate) {
          cubeData.inflate = cube.inflate;
        }

        return cubeData;
      });

    const boneData = {
      name: bone.name,
      pivot: bone.pivot,
      cubes: boneCubes,
    };

    if (bone.parent) {
      boneData.parent = bone.parent;
    }

    if (bone.rotation.some((r) => r !== 0)) {
      boneData.rotation = bone.rotation.map((r) => (r * 180) / Math.PI);
    }

    return boneData;
  });

  return {
    format_version: '1.12.0',
    'minecraft:geometry': [
      {
        description: {
          identifier: identifier.startsWith('geometry.') ? identifier : `geometry.${identifier}`,
          texture_width: resolution.width,
          texture_height: resolution.height,
          visible_bounds_width: 2,
          visible_bounds_height: 2.5,
          visible_bounds_offset: [0, 0.75, 0],
        },
        bones: mcBones,
      },
    ],
  };
};

/**
 * Genera un modelo vacío
 */
export const createEmptyModel = () => {
  return {
    name: 'new_model',
    identifier: 'geometry.new_model',
    resolution: { width: 64, height: 64 },
    useBoxUV: true,
    cubes: [],
    bones: [
      {
        name: 'root',
        id: 'root',
        origin: [0, 0, 0],
        rotation: [0, 0, 0],
        pivot: [0, 0, 0],
        parent: null,
        visible: true,
        cubes: [],
        children: [],
      },
    ],
    texture: null,
    textures: [],
  };
};

/**
 * Añade un nuevo cubo al modelo
 */
export const addCube = (model, cubeOptions = {}) => {
  const {
    name = `cube_${model.cubes.length + 1}`,
    position = [0, 0, 0],
    size = [1, 1, 1],
    parent = 'root',
    uv_offset = [0, 0],
  } = cubeOptions;

  const newCube = {
    id: `cube_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    name,
    from: position,
    to: [position[0] + size[0], position[1] + size[1], position[2] + size[2]],
    size,
    position,
    origin: [0, 0, 0],
    rotation: [0, 0, 0],
    rotationDegrees: [0, 0, 0],
    uv_offset,
    faces: {},
    inflate: 0,
    visible: true,
    locked: false,
    useBoxUV: true,
    parent,
  };

  // Calcular UV para el nuevo cubo
  newCube.faces = calculateBoxUV(newCube.uv_offset, newCube.size, model.resolution);

  const newModel = {
    ...model,
    cubes: [...model.cubes, newCube],
    bones: model.bones.map((b) =>
      b.name === parent ? { ...b, cubes: [...b.cubes, newCube.id] } : b
    ),
  };

  return newModel;
};

/**
 * Duplica un cubo existente en la MISMA posición y con las MISMAS UVs
 * (por defecto). Si se pasa offset, se desplaza esa cantidad.
 */
export const duplicateCube = (model, cubeId, offset = [0, 0, 0]) => {
  const originalCube = model.cubes.find((c) => c.id === cubeId);
  if (!originalCube) return model;

  const newPosition = [
    originalCube.position[0] + offset[0],
    originalCube.position[1] + offset[1],
    originalCube.position[2] + offset[2],
  ];

  return addCube(model, {
    name: `${originalCube.name}_copy`,
    position: newPosition,
    size: originalCube.size,
    parent: originalCube.parent,
    uv_offset: originalCube.uv_offset,
  });
};

/**
 * Añade un bone (grupo) manualmente al modelo
 */
export const addBone = (model, boneName) => {
  const name = boneName || `bone_${model.bones.length + 1}`;
  // Evitar duplicados de nombre
  if (model.bones.some((b) => b.name === name)) {
    return model;
  }
  return {
    ...model,
    bones: [
      ...model.bones,
      {
        name,
        id: `bone_${Date.now()}`,
        origin: [0, 0, 0],
        rotation: [0, 0, 0],
        pivot: [0, 0, 0],
        parent: null,
        visible: true,
        cubes: [],
        children: [],
      },
    ],
  };
};

/**
 * Elimina un cubo
 */
export const deleteCube = (model, cubeId) => {
  return {
    ...model,
    cubes: model.cubes.filter((c) => c.id !== cubeId),
    bones: model.bones.map((b) => ({
      ...b,
      cubes: b.cubes.filter((id) => id !== cubeId),
    })),
  };
};

/**
 * Actualiza propiedades de un cubo
 */
export const updateCube = (model, cubeId, updates) => {
  return {
    ...model,
    cubes: model.cubes.map((c) => {
      if (c.id !== cubeId) return c;
      
      const updated = { ...c, ...updates };
      
      // Recalcular size y to si cambia position o size
      if (updates.size) {
        updated.to = [
          updated.position[0] + updated.size[0],
          updated.position[1] + updated.size[1],
          updated.position[2] + updated.size[2],
        ];
      }
      if (updates.position) {
        updated.from = updated.position;
        updated.to = [
          updated.position[0] + updated.size[0],
          updated.position[1] + updated.size[1],
          updated.position[2] + updated.size[2],
        ];
      }
      
      // Recalcular UV si cambia size o uv_offset
      if (updates.size || updates.uv_offset) {
        updated.faces = calculateBoxUV(updated.uv_offset, updated.size, model.resolution);
      }
      
      return updated;
    }),
  };
};

/**
 * Calcula UV box para un cubo
 */
const calculateBoxUV = (uvOffset, size, resolution) => {
  const [ox, oy] = uvOffset;
  const [w, h, d] = size;
  
  return {
    north: { uv: [ox + d, oy + d, ox + d + w, oy + d + h], texture: 0 },
    south: { uv: [ox + 2 * d + w, oy + d, ox + 2 * d + 2 * w, oy + d + h], texture: 0 },
    east: { uv: [ox, oy + d, ox + d, oy + d + h], texture: 0 },
    west: { uv: [ox + d + w, oy + d, ox + 2 * d + w, oy + d + h], texture: 0 },
    up: { uv: [ox + d, oy, ox + d + w, oy + d], texture: 0 },
    down: { uv: [ox + d + w, oy, ox + d + 2 * w, oy + d], texture: 0 },
  };
};

/**
 * Parsea desde formato Minecraft geometry.json a formato interno
 */
export const parseMinecraftGeometry = (geometryJson) => {
  const geom = geometryJson['minecraft:geometry']?.[0];
  if (!geom) return createEmptyModel();

  const desc = geom.description || {};
  const resolution = {
    width: desc.texture_width || 64,
    height: desc.texture_height || 64,
  };

  const bones = [];
  const cubes = [];

  (geom.bones || []).forEach((bone) => {
    const boneObj = {
      name: bone.name,
      id: bone.name,
      origin: bone.pivot || [0, 0, 0],
      rotation: (bone.rotation || [0, 0, 0]).map((r) => (r * Math.PI) / 180),
      pivot: bone.pivot || [0, 0, 0],
      parent: bone.parent || null,
      visible: true,
      cubes: [],
      children: [],
    };

    (bone.cubes || []).forEach((cube, idx) => {
      const cubeId = `${bone.name}_cube_${idx}`;
      const from = cube.origin || [0, 0, 0];
      const size = cube.size || [1, 1, 1];
      
      let useBoxUV = true;
      let uv_offset = [0, 0];
      let faces = {};
      
      if (Array.isArray(cube.uv)) {
        uv_offset = cube.uv;
        useBoxUV = true;
        faces = calculateBoxUV(uv_offset, size, resolution);
      } else if (typeof cube.uv === 'object') {
        useBoxUV = false;
        Object.entries(cube.uv).forEach(([face, data]) => {
          const uv = data.uv || [0, 0];
          const uvSize = data.uv_size || [1, 1];
          faces[face] = {
            uv: [uv[0], uv[1], uv[0] + uvSize[0], uv[1] + uvSize[1]],
            texture: 0,
          };
        });
      }

      cubes.push({
        id: cubeId,
        name: `cube_${idx}`,
        from,
        to: [from[0] + size[0], from[1] + size[1], from[2] + size[2]],
        size,
        position: from,
        origin: cube.pivot || [0, 0, 0],
        rotation: (cube.rotation || [0, 0, 0]).map((r) => (r * Math.PI) / 180),
        rotationDegrees: cube.rotation || [0, 0, 0],
        uv_offset,
        faces,
        inflate: cube.inflate || 0,
        visible: true,
        locked: false,
        useBoxUV,
        parent: bone.name,
      });

      boneObj.cubes.push(cubeId);
    });

    bones.push(boneObj);
  });

  return {
    name: desc.identifier?.replace('geometry.', '') || 'model',
    identifier: desc.identifier || 'geometry.model',
    resolution,
    useBoxUV: true,
    cubes,
    bones,
    texture: null,
    textures: [],
  };
};
